import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { PermissionService } from './Services/PermissionService.js';
import './styles/PermissionManagement.css';
import '../../assets/css/StudentDashboard.css';
import SearchBar from '../../Component/SearchBar';
import Table from '../../Component/Table';
import PermissionSuccessModal from './Modals/PermissionSuccessModal';
import CreatePermissionModal from './Modals/CreatePermissionModal';
import UserPermissionModal from './Modals/UserPermissionModal';
import add from '../../assets/icons/add.svg';
import filtericon from '../../assets/icons/mage_filter-fill.svg';
import assignIcon from '../../assets/icons/settings.svg';

const PermissionManagement = () => {
  const [moduleTree, setModuleTree] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isUserPermissionModalOpen, setIsUserPermissionModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [currentView, setCurrentView] = useState('modules'); // 'modules' | 'users' | 'stats'
  const [expandedModules, setExpandedModules] = useState(new Set());
  const [expandedSubmodules, setExpandedSubmodules] = useState(new Set());
  const [groupedUsers, setGroupedUsers] = useState({});
  const [currentProfileTab, setCurrentProfileTab] = useState('');
  const [profileTabs, setProfileTabs] = useState([]);

  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);

      // Phase 1: Load critical UI data first (modules for navigation)
      const treeData = await PermissionService.getModuleTree();
      setModuleTree(treeData || []);

      // Show basic UI immediately after modules load
      setLoading(false);

      // Phase 2: Load user data (most important for functionality)
      const groupedUsersData = await PermissionService.getUsersGroupedByProfile();
      setGroupedUsers(groupedUsersData || {});

      // Set up profile tabs
      const profiles = Object.keys(groupedUsersData || {});
      setProfileTabs(profiles);
      if (profiles.length > 0 && !currentProfileTab) {
        setCurrentProfileTab(profiles[0]);
      }

      // Set users for backward compatibility - optimized processing
      const allUsers = [];
      Object.values(groupedUsersData || {}).forEach(profileGroup => {
        if (profileGroup?.users) {
          allUsers.push(...profileGroup.users);
        }
      });
      setUsers(allUsers);

      // Phase 3: Load stats data last (least critical for initial experience)
      try {
        const statsData = await PermissionService.getPermissionStats();
        setStats(statsData || {});
      } catch (statsError) {
        console.warn('Stats loading failed, continuing without stats:', statsError);
        setStats({});
      }

    } catch (error) {
      console.error('Error loading initial data:', error);
      setSuccessMessage('Error loading data. Please refresh the page.');
      setShowSuccessPopup(true);
      setLoading(false);
    }
  }, [currentProfileTab]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // Auto-dismiss success popup after 3 seconds
  useEffect(() => {
    if (showSuccessPopup) {
      const timer = setTimeout(() => {
        setShowSuccessPopup(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showSuccessPopup]);

  const handleCreatePermission = async (permissionData) => {
    try {
      await PermissionService.createPermission(permissionData);
      setSuccessMessage('Permission created successfully!');
      setShowSuccessPopup(true);
      setIsCreateModalOpen(false);
      // Only reload module tree since we created a new permission
      const treeData = await PermissionService.getModuleTree();
      setModuleTree(treeData || []);
    } catch (error) {
      console.error('Error creating permission:', error);
      setSuccessMessage('Error creating permission. Please try again.');
      setShowSuccessPopup(true);
    }
  };

  const handleAssignPermissions = async (assignmentData) => {
    try {
      await PermissionService.bulkAssignPermissions(assignmentData);
      setSuccessMessage('Permissions assigned successfully!');
      setShowSuccessPopup(true);
      setIsUserPermissionModalOpen(false);
      // Only reload user data since we assigned permissions
      const groupedUsersData = await PermissionService.getUsersGroupedByProfile();
      setGroupedUsers(groupedUsersData || {});

      // Update users for backward compatibility
      const allUsers = [];
      Object.values(groupedUsersData || {}).forEach(profileGroup => {
        allUsers.push(...profileGroup.users);
      });
      setUsers(allUsers);
    } catch (error) {
      console.error('Error assigning permissions:', error);
      setSuccessMessage('Error assigning permissions. Please try again.');
      setShowSuccessPopup(true);
    }
  };

  const handleRevokePermissions = async (revokeData) => {
    try {
      await PermissionService.bulkAssignPermissions(revokeData);
      setSuccessMessage('Permissions revoked successfully!');
      setShowSuccessPopup(true);

      // Only reload user data since we revoked permissions
      const groupedUsersData = await PermissionService.getUsersGroupedByProfile();
      setGroupedUsers(groupedUsersData || {});

      // Update users for backward compatibility
      const allUsers = [];
      Object.values(groupedUsersData || {}).forEach(profileGroup => {
        allUsers.push(...profileGroup.users);
      });
      setUsers(allUsers);
    } catch (error) {
      console.error('Error revoking permissions:', error);
      setSuccessMessage('Error revoking permissions. Please try again.');
      setShowSuccessPopup(true);
    }
  };

  const openUserPermissionModal = useCallback((user) => {
    setSelectedUser(user);
    setIsUserPermissionModalOpen(true);
  }, []);

  const toggleModule = (moduleId) => {
    const newExpanded = new Set(expandedModules);
    if (newExpanded.has(moduleId)) {
      newExpanded.delete(moduleId);
    } else {
      newExpanded.add(moduleId);
    }
    setExpandedModules(newExpanded);
  };

  const toggleSubmodule = (submoduleId) => {
    const newExpanded = new Set(expandedSubmodules);
    if (newExpanded.has(submoduleId)) {
      newExpanded.delete(submoduleId);
    } else {
      newExpanded.add(submoduleId);
    }
    setExpandedSubmodules(newExpanded);
  };

  const getUserCountForPermission = useCallback((permissionId) => {
    // Count how many users have this permission
    return users.filter(user =>
      user.permissions?.some(perm => perm.id === permissionId)
    ).length;
  }, [users]);

  const getCurrentProfileUsers = useMemo(() => {
    if (!currentProfileTab || !groupedUsers[currentProfileTab]) {
      return [];
    }

    const profileUsers = groupedUsers[currentProfileTab].users;

    // Apply search filter
    return profileUsers.filter(user => {
      if (!searchQuery) return true;

      return (
        user.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.mobile?.includes(searchQuery) ||
        user.permissions?.some(perm =>
          perm.name?.toLowerCase().includes(searchQuery.toLowerCase())
        ) ||
        (user.student_info?.registration_number?.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (user.student_info?.permanent_registration_number?.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }).map(user => ({
      ...user,
      handleRowClick: (userId, row) => {

        openUserPermissionModal(user);
      }
    }));
  }, [currentProfileTab, groupedUsers, searchQuery, openUserPermissionModal]);

  // Filter modules based on search query
  const filteredModules = useMemo(() => {
    return moduleTree.filter(module =>
      module.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      module.submodules?.some(sub =>
        sub.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        sub.permissions?.some(perm =>
          perm.name?.toLowerCase().includes(searchQuery.toLowerCase())
        )
      )
    );
  }, [moduleTree, searchQuery]);

  // Table columns for users
  const userColumns = [
    {
      Header: 'Name',
      accessor: 'full_name',
    },
    {
      Header: 'Mobile',
      accessor: 'mobile',
    },
    {
      Header: 'Profile',
      accessor: 'profile',
      Cell: ({ value }) => (
        <span className="profile-badge">
          {value?.name || 'N/A'}
        </span>
      ),
    },
    {
      Header: 'Registration',
      accessor: 'student_info',
      Cell: ({ value, row }) => {
        if (value && value.registration_number) {
          return (
            <div className="registration-info">
              <div className="reg-number">{value.registration_number}</div>
              {value.permanent_registration_number && (
                <div className="perm-reg-number">{value.permanent_registration_number}</div>
              )}
            </div>
          );
        }
        return <span className="no-registration">-</span>;
      },
    },
    {
      Header: 'Status',
      accessor: 'is_active',
      Cell: ({ value }) => (
        <span className={`status-badge ${value ? 'active' : 'inactive'}`}>
          {value ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      Header: 'Permissions Count',
      accessor: 'permissions',
      Cell: ({ value }) => (
        <span className="permission-count">{value?.length || 0}</span>
      ),
    },
    {
      Header: 'Actions',
      Cell: ({ row }) => (
        <div className="table-actions">
          <button
            onClick={() => openUserPermissionModal(row.original)}
            className="action-btn assign-btn"
            title="Manage Permissions"
          >
            <img src={assignIcon} alt="Assign" />
          </button>
        </div>
      ),
    },
  ];

  if (loading) {
    return <div className="loading-spinner">Loading...</div>;
  }

  return (
    <div className="permission-management" style={{
      display: "flex",
      flexDirection: "column",
      height: "100vh",
      overflow: "hidden",
      position: "relative",
    }}>
      <div className="page-header" style={{ flexShrink: 0, padding: "0 12px" }}>
        <div className="header-content">
          <h1 className="page-title">Permission Management</h1>
          <div className="header-actions">
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="primary-btn add-btn"
            >
              <img src={add} alt="Add" />
              Create Permission
            </button>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div style={{ flexShrink: 0, padding: "0 12px" }}>
        <div className="nav-tabs">
          <button
            className={`nav-tab ${currentView === 'modules' ? 'active' : ''}`}
            onClick={() => setCurrentView('modules')}
          >
            Modules & Permissions
          </button>
          <button
            className={`nav-tab ${currentView === 'users' ? 'active' : ''}`}
            onClick={() => setCurrentView('users')}
          >
            User Permissions
          </button>
          <button
            className={`nav-tab ${currentView === 'stats' ? 'active' : ''}`}
            onClick={() => setCurrentView('stats')}
          >
            Statistics
          </button>
        </div>
      </div>
      <div style={{ flexShrink: 0, padding: "8px 12px" }}>
        {/* Search and Filter Section */}
        <div className="search-filter-section">
          <SearchBar
            onSearch={setSearchQuery}
            placeholder={`Search ${currentView}...`}
          />
          <div className="filter-btn">
            <img src={filtericon} alt="Filter" />
          </div>
        </div>
      </div>
      <div style={{
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        overflowX: "hidden",
        padding: "0 12px 12px 12px",
      }}>
        {/* Content based on current view */}
        {currentView === 'modules' && (
          <div className="modules-view">
            <div className="nested-tree-container">
              {filteredModules.map((module) => (
                <div key={module.id} className="tree-item module-item">
                  <div
                    className="tree-header module-header"
                    onClick={() => toggleModule(module.id)}
                  >
                    <div className="tree-toggle">
                      <span className={`toggle-icon ${expandedModules.has(module.id) ? 'expanded' : ''}`}>
                        ▶
                      </span>
                    </div>
                    <div className="tree-content">
                      <span className="tree-title">{module.name}</span>
                      <span className="tree-count">
                        {module.submodules?.length || 0} submodules
                      </span>
                    </div>
                  </div>

                  {expandedModules.has(module.id) && (
                    <div className="tree-children">
                      {module.submodules?.map((submodule) => (
                        <div key={submodule.id} className="tree-item submodule-item">
                          <div
                            className="tree-header submodule-header"
                            onClick={() => toggleSubmodule(submodule.id)}
                          >
                            <div className="tree-toggle">
                              <span className={`toggle-icon ${expandedSubmodules.has(submodule.id) ? 'expanded' : ''}`}>
                                ▶
                              </span>
                            </div>
                            <div className="tree-content">
                              <span className="tree-title">{submodule.name}</span>
                              <span className="tree-count">
                                {submodule.permissions?.length || 0} permissions
                              </span>
                            </div>
                          </div>

                          {expandedSubmodules.has(submodule.id) && (
                            <div className="tree-children">
                              {submodule.permissions?.map((permission) => (
                                <div key={permission.id} className="tree-item permission-item">
                                  <div className="tree-header permission-header">
                                    <div className="tree-toggle">
                                      <span className="permission-bullet">•</span>
                                    </div>
                                    <div className="tree-content">
                                      <span className="tree-title">{permission.name}</span>
                                      <span className="permission-code">({permission.code})</span>
                                      <span className="tree-count user-count">
                                        {getUserCountForPermission(permission.id)} users
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {currentView === 'users' && (
          <div className="users-view">
            {/* Profile Sub-tabs */}
            <div className="profile-tabs">
              {profileTabs.map((profile) => (
                <button
                  key={profile}
                  className={`profile-tab ${currentProfileTab === profile ? 'active' : ''}`}
                  onClick={() => setCurrentProfileTab(profile)}
                >
                  {profile} ({groupedUsers[profile]?.users_count || 0})
                </button>
              ))}
            </div>

            {/* Current Profile Users */}
            {currentProfileTab && groupedUsers[currentProfileTab] && (
              <div className="profile-users">
                <div className="profile-header">
                  <h3>{groupedUsers[currentProfileTab].profile.name}</h3>
                  <p>{groupedUsers[currentProfileTab].profile.description}</p>
                  <div className="profile-stats">
                    <span className="user-count">
                      Total Users: {groupedUsers[currentProfileTab].users_count}
                    </span>
                    <span className="users-with-permissions">
                      With Permissions: {groupedUsers[currentProfileTab].users.filter(user => user.permissions.length > 0).length}
                    </span>
                  </div>
                </div>

                <div className="table-container">
                  <Table
                    data={getCurrentProfileUsers}
                    columns={userColumns}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {currentView === 'stats' && (
          <div className="stats-view">
            <div className="stats-header">
              <h2>Permission System Overview</h2>
              <p>Monitor and analyze the permission management system</p>
            </div>

            {stats.popular_permissions && stats.popular_permissions.length > 0 && (
              <div className="popular-permissions">
                <h3>Most Popular Permissions</h3>
                <div className="permissions-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Permission Name</th>
                        <th>Code</th>
                        <th>Users Assigned</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.popular_permissions.map((perm, index) => (
                        <tr key={index}>
                          <td>{perm.name}</td>
                          <td><code>{perm.code}</code></td>
                          <td>
                            <span className="user-count-badge">
                              {perm.user_count} users
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}</div>

      {/* Modals */}
      {isCreateModalOpen && (
        <CreatePermissionModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSave={handleCreatePermission}
          moduleTree={moduleTree}
        />
      )}

      {isUserPermissionModalOpen && (
        <UserPermissionModal
          isOpen={isUserPermissionModalOpen}
          onClose={() => setIsUserPermissionModalOpen(false)}
          onSave={handleAssignPermissions}
          onRevokePermissions={handleRevokePermissions}
          user={selectedUser}
          moduleTree={moduleTree}
        />
      )}

      {showSuccessPopup && (
        <PermissionSuccessModal
          message={successMessage}
          onClose={() => setShowSuccessPopup(false)}
          autoClose={true}
          autoCloseDelay={3000}
        />
      )}
    </div>
  );
};

export default PermissionManagement;
